import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import api from "../../utils/apis";
import {
  Package,
  Calendar,
  ChevronRight,
  Truck,
  CheckCircle,
  Clock,
  XCircle,
  Copy,
  ChevronDown,
  AlertCircle,
} from "lucide-react";
import "./order.css";
import toast from "react-hot-toast";
import MexaLoader from "../../components/MexaLoader/MexaLoader";

const Orders = () => {
  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [copiedOrderId, setCopiedOrderId] = useState(null);
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [expandedSections, setExpandedSections] = useState({});
  const navigate = useNavigate();
  const { userData } = useAuth();
  const userId = userData?.id;

  useEffect(() => {
    fetchOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  // --- Date grouping helpers ---
  const getDateGroup = (dateString) => {
    if (!dateString) return "Other";

    const orderDate = new Date(dateString);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    const startOfWeek = new Date(today);
    startOfWeek.setDate(today.getDate() - today.getDay());

    const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    const startOfLastMonth = new Date(
      today.getFullYear(),
      today.getMonth() - 1,
      1,
    );

    today.setHours(0, 0, 0, 0);
    yesterday.setHours(0, 0, 0, 0);
    startOfWeek.setHours(0, 0, 0, 0);
    startOfMonth.setHours(0, 0, 0, 0);
    startOfLastMonth.setHours(0, 0, 0, 0);
    orderDate.setHours(0, 0, 0, 0);

    if (orderDate.getTime() === today.getTime()) return "Today";
    if (orderDate.getTime() === yesterday.getTime()) return "Yesterday";
    if (orderDate >= startOfWeek) return "This Week";
    if (orderDate >= startOfMonth) return "This Month";
    if (orderDate >= startOfLastMonth) return "Last Month";

    return orderDate.toLocaleDateString("en-US", {
      month: "long",
      year: "numeric",
    });
  };

  const getGroupTitle = (group) => {
    const titles = {
      Today: "Today",
      Yesterday: "Yesterday",
      "This Week": "This Week",
      "This Month": "This Month",
      "Last Month": "Last Month",
      Other: "Other Orders",
    };
    return titles[group] || group;
  };

  const toggleSection = (group) => {
    setExpandedSections((prev) => ({
      ...prev,
      [group]: !prev[group],
    }));
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Date not available";
    const date = new Date(dateString);
    if (isNaN(date)) return dateString;
    return date.toLocaleDateString("en-US", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  const mapStatus = (dbStatus) => {
    if (!dbStatus) return "PROCESSING";
    const s = String(dbStatus).trim().toLowerCase();
    const mapping = {
      delivered: "DELIVERED",
      shipped: "SHIPPED",
      processing: "PROCESSING",
      placed: "PLACED",
      cancelled: "CANCELLED",
      packed: "PACKED",
      returned: "RETURNED",
      refunded: "REFUNDED",
      rto: "RTO",
      out_for_delivery: "OUT_FOR_DELIVERY",
    };
    return mapping[s] || "UNKNOWN";
  };

  const getStatusConfig = (statusOrRaw) => {
    const uiKey =
      typeof statusOrRaw === "string" &&
        statusOrRaw.trim().length &&
        !/^[A-Z_]+$/.test(statusOrRaw)
        ? mapStatus(statusOrRaw)
        : String(statusOrRaw || "UNKNOWN")
          .trim()
          .toUpperCase();

    const configs = {
      DELIVERED: {
        color:
          "text-green-800 bg-gradient-to-r from-green-50 to-green-100 border border-green-200",
        iconBg: "bg-gradient-to-br from-green-500 to-green-700 shadow-sm",
        icon: <CheckCircle className="w-6 h-6 text-white" />,
        label: "Delivered",
      },
      PROCESSING: {
        color:
          "text-blue-800 bg-gradient-to-r from-blue-50 to-blue-100 border border-blue-200",
        iconBg:
          "bg-gradient-to-br from-blue-500 via-blue-600 to-blue-700 shadow-sm",
        icon: <Clock className="w-6 h-6 text-white" />,
        label: "Processing",
      },
      PLACED: {
        color:
          "text-amber-800 bg-gradient-to-r from-amber-50 to-amber-100 border border-amber-200",
        iconBg:
          "bg-gradient-to-br from-amber-400 via-amber-500 to-amber-600 shadow-sm",
        icon: <Package className="w-6 h-6 text-white" />,
        label: "Order Placed",
      },
      CANCELLED: {
        color:
          "text-red-800 bg-gradient-to-r from-red-50 to-red-100 border border-red-200",
        iconBg:
          "bg-gradient-to-br from-red-500 via-red-600 to-red-700 shadow-sm",
        icon: <XCircle className="w-6 h-6 text-white" />,
        label: "Cancelled",
      },
      SHIPPED: {
        color:
          "text-indigo-800 bg-gradient-to-r from-indigo-50 to-indigo-100 border border-indigo-200",
        iconBg:
          "bg-gradient-to-br from-indigo-500 via-indigo-600 to-purple-700 shadow-sm",
        icon: <Truck className="w-6 h-6 text-white" />,
        label: "Shipped",
      },
      OUT_FOR_DELIVERY: {
        color:
          "text-gray-700 bg-gradient-to-r from-gray-50 to-gray-100 border border-gray-200",
        iconBg:
          "bg-gradient-to-br from-gray-400 via-gray-500 to-gray-600 shadow-sm",
        icon: <Truck className="w-6 h-6 text-white" />,
        label: "Out for Delivery",
      },
      UNKNOWN: {
        color: "text-gray-800 bg-gray-50 border border-gray-200",
        iconBg: "bg-gray-400",
        icon: <AlertCircle className="w-6 h-6 text-white" />,
        label: "Status Pending",
      },
    };
    return configs[uiKey] || configs.UNKNOWN;
  };

  const mapRawOrder = (order) => {
    const idStr =
      order.id != null
        ? String(order.id)
        : order.order_code || Math.random().toString(36).slice(2);
    const code = order.order_code || idStr;
    const statusRaw = order.order_status ?? "";
    const status = mapStatus(statusRaw);
    const date = formatDate(order.order_date);
    const amount = Number(order.total ?? 0);
    const dateGroup = getDateGroup(order.order_date);

    return {
      id: idStr,
      code,
      status,
      statusRaw,
      date,
      amount,
      dateGroup,
      payment_method: order.payment_method,
      payment_status: order.payment_status,
      transaction_status: order.transaction_status,
      razorpay_payment_id: order.razorpay_payment_id,
      raw: order,
    };
  };

  const copyToClipboard = async (text, e) => {
    if (e && typeof e.stopPropagation === "function") e.stopPropagation();
    try {
      await navigator.clipboard.writeText(text);
      setCopiedOrderId(text);
      setTimeout(() => setCopiedOrderId(null), 2000);
    } catch (err) {
      console.error("Copy failed:", err);
    }
  };

  const handleRefresh = () => {
    fetchOrders();
    toast.success("Orders refreshed");
  };

  const fetchOrders = async () => {
    try {
      if (!userId) {
        setLoading(false);
        setOrders([]);
        return;
      }
      setLoading(true);
      const response = await api.get(`/customer/orders/${userId}`);
      const data = Array.isArray(response.data) ? response.data : [];
      const transformed = data.map(mapRawOrder);
      transformed.sort(
        (a, b) => new Date(b.raw.order_date) - new Date(a.raw.order_date),
      );
      setOrders(transformed);
    } catch (err) {
      setError("Failed to load orders");
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = (orderId) => {
    navigate(`/orders/${orderId}`);
  };

  const filteredOrders = useMemo(() => {
    if (statusFilter === "ALL") return orders;
    return orders.filter((order) => order.status === statusFilter);
  }, [orders, statusFilter]);

  const groupedOrders = useMemo(() => {
    const grouped = filteredOrders.reduce((acc, order) => {
      const group = order.dateGroup;
      if (!acc[group]) acc[group] = [];
      acc[group].push(order);
      return acc;
    }, {});

    const groupOrder = [
      "Today",
      "Yesterday",
      "This Week",
      "This Month",
      "Last Month",
    ];
    const sortedGroups = {};
    groupOrder.forEach((group) => {
      if (grouped[group]) sortedGroups[group] = grouped[group];
    });
    Object.keys(grouped).forEach((group) => {
      if (!groupOrder.includes(group)) sortedGroups[group] = grouped[group];
    });

    // Expansion logic: Expand the first group automatically
    const groups = Object.keys(sortedGroups);
    if (groups.length > 0) {
      setExpandedSections((prev) => ({
        ...prev,
        [groups[0]]: true,
      }));
    }

    return sortedGroups;
  }, [filteredOrders]);

  const statusFilters = [
    { key: "ALL", label: "All" },
    { key: "PLACED", label: "Placed" },
    { key: "PROCESSING", label: "Processing" },
    { key: "SHIPPED", label: "Shipped" },
    { key: "DELIVERED", label: "Delivered" },
  ];
  return (
    <div className="view-section p-3">
      <div className="flex items-center justify-between mb-1">
        <div className="mb-5">
          <h2 className="md:text-2xl text-xl font-bold text-gray-900 mb-1">
            Your Orders
          </h2>
          <p className="text-gray-600 text-xs">
            Track and manage your recent purchases
          </p>
        </div>
        <button
          onClick={handleRefresh}
          disabled={loading}
          className={`
      inline-flex items-center gap-1.5
      px-3 py-1.5
      text-xs font-medium
      text-gray-700
      bg-gray-100 hover:bg-gray-200
      active:bg-gray-300
      rounded-lg
      transition-colors
      border border-gray-200
      ${loading ? "opacity-50 cursor-not-allowed" : ""}
    `}
        >
          {loading ? "Refreshing..." : "Refresh"}
        </button>
      </div>

      <div className="w-full mb-4 px-1 overflow-x-auto .hide-scrollbar">
        <div className="flex flex-nowrap gap-2 min-w-max pb-3">
          {statusFilters.map((filter) => (
            <button
              key={filter.key}
              onClick={() => setStatusFilter(filter.key)}
              className={`px-4 py-2 rounded-lg text-xs font-medium transition-colors ${statusFilter === filter.key
                  ? "bg-black text-white"
                  : "bg-gray-100 text-gray-700"
                }`}
            >
              {filter.label}
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-6">
        {loading && orders.length === 0 ? (
          <div className="flex justify-center py-10">
            <MexaLoader size="lg" text="Loading orders..." />
          </div>
        ) : Object.keys(groupedOrders).length > 0 ? (
          Object.entries(groupedOrders).map(([group, groupOrders]) => (
            <div
              key={group}
              className="overflow-hidden border rounded-lg bg-white mb-4"
            >
              <div
                className="flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 cursor-pointer"
                onClick={() => toggleSection(group)}
              >
                <div className="flex items-center space-x-3">
                  <h3 className="font-semibold text-gray-900 text-sm">
                    {getGroupTitle(group)}
                  </h3>
                  <span className="bg-gray-300 text-gray-700 text-xs px-2 py-1 rounded-full">
                    {groupOrders.length}
                  </span>
                </div>
                <ChevronDown
                  className={`w-4 h-4 transition-transform ${expandedSections[group] ? "rotate-180" : ""}`}
                />
              </div>

              {expandedSections[group] && (
                <div className="divide-y">
                  {groupOrders.map((order) => {
                    const statusConfig = getStatusConfig(order.statusRaw);
                    return (
                      <div
                        key={order.id}
                        className="p-3 hover:bg-gray-50 transition-colors cursor-pointer"
                        onClick={() => handleViewDetails(order.id)}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex items-start space-x-3">
                            <div
                              className={`w-10 h-10 ${statusConfig.iconBg} rounded-full flex items-center justify-center shrink-0`}
                            >
                              {statusConfig.icon}
                            </div>
                            <div className="min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="text-sm font-semibold text-gray-900">
                                  Order #{order.code}
                                </span>
                                <button
                                  onClick={(e) =>
                                    copyToClipboard(order.code, e)
                                  }
                                  className="text-gray-400"
                                >
                                  {copiedOrderId === order.code ? (
                                    <CheckCircle
                                      size={14}
                                      className="text-green-500"
                                    />
                                  ) : (
                                    <Copy size={14} />
                                  )}
                                </button>
                              </div>
                              <div className="flex flex-wrap gap-2 text-[10px]">
                                <span className="font-bold text-blue-600 capitalize">
                                  {order.payment_method
                                    .replace(/_/g, " ")
                                    .replace("razorpay", "Razorpay")
                                    .replace("cod", "COD")}
                                </span>
                                <span className="text-gray-400">|</span>
                                <span className="capitalize text-gray-600">
                                  Payment: {order.payment_status}
                                </span>
                                {order.transaction_status && (
                                  <>
                                    <span className="text-gray-400">|</span>
                                    <span className="text-gray-600">
                                      Txn: {order.transaction_status}
                                    </span>
                                  </>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="text-right ml-4">
                            <div className="font-extrabold text-gray-900 text-xs">
                              ₹{order.amount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </div>
                            <div
                              className={`text-[10px] px-2 py-0.5 rounded mt-1 inline-block font-medium ${statusConfig.color}`}
                            >
                              {order.statusRaw}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ))
        ) : (
          <div className="text-center py-10">
            <Package className="mx-auto text-gray-300 mb-2" size={40} />
            <p className="text-gray-500 text-sm">No orders found.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Orders;
